import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  InputLabel,
  Select,
  FormControl,
  MenuItem,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import {
  createApprovalRequest,
  type CreateApprovalRequestResult,
} from '../services/approvalService';
import { GLASS } from '../theme/glassTokens';

interface ApprovalRequestDialogProps {
  open: boolean;
  onClose: () => void;
  clientId: string;
  postIds: string[];
  onCreated?: () => void;
  onGoToLinks?: () => void;
}

const EXPIRY_OPTIONS = [
  { label: '7 dias', days: 7 },
  { label: '15 dias', days: 15 },
  { label: '30 dias', days: 30 },
];

const ApprovalRequestDialog: React.FC<ApprovalRequestDialogProps> = ({
  open,
  onClose,
  clientId,
  postIds,
  onCreated,
  onGoToLinks,
}) => {
  const [label, setLabel] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateApprovalRequestResult | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await createApprovalRequest(clientId, postIds, expiresInDays, label || undefined);
      setResult(res);
      onCreated?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao criar solicitação de aprovação.');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Não foi possível copiar o link. Copie manualmente.');
    }
  };

  const handleOpenLink = () => {
    if (!result?.url) return;
    window.open(result.url, '_blank', 'noopener,noreferrer');
  };

  const handleGoToLinks = () => {
    onGoToLinks?.();
    handleClose();
  };

  const handleClose = () => {
    setResult(null);
    setLabel('');
    setExpiresInDays(7);
    setError(null);
    setCopied(false);
    onClose();
  };

  const showResult = result !== null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{
        sx: {
          borderRadius: GLASS.radius.card,
          bgcolor: GLASS.surface.bgStrong,
          backdropFilter: `blur(${GLASS.surface.blurStrong})`,
          WebkitBackdropFilter: `blur(${GLASS.surface.blurStrong})`,
          border: `1px solid ${GLASS.border.outer}`,
          boxShadow: `${GLASS.shadow.card}, ${GLASS.shadow.cardInset}`,
        }
      }}
    >
      <DialogTitle>
        {showResult ? 'Link de aprovação criado' : 'Enviar para aprovação do cliente'}
      </DialogTitle>
      <DialogContent>
        {!showResult ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Você está <strong>enviando</strong> estes posts para aprovação. Depois de criar o link, envie-o ao cliente; <strong>ele</strong> abrirá o link e aprovará ou solicitará alterações.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {postIds.length} postagem(ns) será(ão) incluída(s) na solicitação.
            </Typography>
            <TextField
              label="Rótulo (opcional)"
              placeholder="Ex.: Posts março"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              fullWidth
              size="small"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Validade do link</InputLabel>
              <Select
                value={expiresInDays}
                label="Validade do link"
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
              >
                {EXPIRY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.days} value={opt.days}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Envie o link abaixo ao cliente. Depois, o cliente abrirá no celular, verá o preview das postagens e aprovará ou solicitará alterações.
            </Typography>
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>
                {error}
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                bgcolor: 'action.hover',
                borderRadius: 1,
                wordBreak: 'break-all',
              }}
            >
              <Typography variant="body2" component="span" sx={{ flex: 1 }}>
                {result!.url}
              </Typography>
              <IconButton size="small" onClick={handleCopy} title="Copiar link">
                <CopyIcon fontSize="small" />
              </IconButton>
            </Box>
            {copied && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                Link copiado!
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={handleOpenLink}>
                Abrir link
              </Button>
              <Button variant="contained" onClick={handleGoToLinks}>
                Ir para Links ativos
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!showResult ? (
          <>
            <Button onClick={handleClose} disabled={creating}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating || postIds.length === 0}
              startIcon={creating ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                bgcolor: GLASS.accent.orange,
                borderRadius: GLASS.radius.button,
                '&:hover': { bgcolor: GLASS.accent.orangeDark },
              }}
            >
              {creating ? 'Criando...' : 'Criar e gerar link'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleClose}>
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalRequestDialog;
